// src/components/notifications/Cloche.tsx — SCE Issues
// La cloche de l'en-tête (03/11, maquette validée :
// https://claude.ai/artifact/AG5cv9AuMJZ68Yy5UgWW9M), à droite de Rafraîchir.
//
// Pastille ROUGE = le nombre de notifications qui restent (pas de « lu » :
// elles sont éphémères, utils/cloche). Rouge et non jaune (Guillaume, 03/11) :
// le jaune disparaissait sur l'en-tête jaune de l'accueil. La cloche sonne une
// fois quand le nombre augmente (`.cloche-sonne`, comme les cloches « Suivre »). Au bureau, un panneau sous la cloche ;
// au téléphone, un écran entier. Chaque ligne : la photo de l'auteur et l'icône
// du geste en médaillon (comme l'activité), « **Auteur** quoi **référence** »,
// le libellé, l'extrait sur fond gris, « il y a … · raison ». Un clic l'ouvre et
// l'efface ; la croix l'efface seule ; « Tout effacer » vide la cloche.
//
// Rien tant que la liste SCE_Notifications n'est pas lue (ou n'existe pas).
// L'état vient de features/cloche, tenu à jour par ChargeurCloche.

import { useEffect, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  AtSign,
  Bell,
  CheckCircle2,
  ChevronLeft,
  Eye,
  Flag,
  MessageSquare,
  Plus,
  Send,
  SlidersHorizontal,
  Tag,
  X,
  XCircle,
} from 'lucide-react'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { BUREAU, useMediaQuery } from '@/hooks/useMediaQuery'
import { effacerDeLaCloche, useCloche } from '@/features/cloche/etat'
import {
  DUREE_VIE_JOURS,
  iconeNotification,
  parJour,
  piedNotification,
  type IconeNotification,
  type NotificationAppli,
} from '@/utils/cloche'

/** Les fiches qui s'ouvrent par-dessus la page au bureau (App.tsx, FICHES_MODALES). */
const PREFIXES_FICHES = ['/issues/', '/equipements/', '/projets/', '/chefs-projet/', '/fnct/']

const MEDAILLONS: Record<IconeNotification, { icone: JSX.Element; fond: string; couleur: string }> = {
  mention: { icone: <AtSign size={11} strokeWidth={3} />, fond: 'var(--color-black)', couleur: 'var(--color-white)' },
  declaration: { icone: <Plus size={11} strokeWidth={3.4} />, fond: 'var(--color-yellow)', couleur: 'var(--color-black)' },
  clos: { icone: <CheckCircle2 size={11} strokeWidth={3} />, fond: 'var(--color-traffic-green)', couleur: 'var(--color-white)' },
  annule: { icone: <XCircle size={11} strokeWidth={3} />, fond: 'var(--color-gray-50)', couleur: 'var(--color-white)' },
  fnct: { icone: <Flag size={11} strokeWidth={3} />, fond: 'var(--color-black)', couleur: 'var(--color-white)' },
  be: { icone: <Send size={11} strokeWidth={3} />, fond: 'var(--color-gray-80)', couleur: 'var(--color-white)' },
  qualification: { icone: <Tag size={11} strokeWidth={3} />, fond: 'var(--color-yellow)', couleur: 'var(--color-black)' },
  commentaire: { icone: <MessageSquare size={11} strokeWidth={3} />, fond: 'var(--color-gray-80)', couleur: 'var(--color-white)' },
}

const PETITES_CAPITALES = {
  fontSize: 12,
  color: 'var(--color-text-secondary)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
} as const

function Ligne({
  n,
  maintenant,
  lectureSeule,
  telephone,
  onOuvrir,
}: {
  n: NotificationAppli
  maintenant: Date
  lectureSeule: boolean
  telephone: boolean
  onOuvrir: (n: NotificationAppli) => void
}): JSX.Element {
  const m = MEDAILLONS[iconeNotification(n)]
  const taille = telephone ? 44 : 40
  return (
    <div className="ligne-cloche" style={{ position: 'relative', borderBottom: telephone ? '1px solid var(--color-gray-10)' : 'none' }}>
      <button
        type="button"
        onClick={() => onOuvrir(n)}
        className="flex items-start text-left w-full"
        style={{
          gap: 12,
          padding: telephone ? '14px 52px 14px 16px' : '12px 48px 12px 18px',
          border: 'none',
          background: 'transparent',
          fontFamily: 'inherit',
          color: 'var(--color-text-primary)',
          cursor: n.lien ? 'pointer' : 'default',
        }}
      >
        <span aria-hidden="true" style={{ position: 'relative', flexShrink: 0, width: taille, height: taille }}>
          <PastillePersonne nom={n.auteur || 'SCE Issues'} upn={n.auteurUpn || undefined} taille={taille} taillePolice={14} />
          <span
            className="flex items-center justify-center"
            style={{
              position: 'absolute',
              right: -3,
              bottom: -3,
              width: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: m.fond,
              color: m.couleur,
              boxShadow: '0 0 0 2px var(--color-white)',
            }}
          >
            {m.icone}
          </span>
        </span>
        <span className="flex flex-col" style={{ gap: 3, flexGrow: 1, minWidth: 0 }}>
          <span style={{ fontSize: telephone ? 15 : 14, lineHeight: telephone ? '20px' : '19px' }}>
            {n.auteur && <b>{n.auteur}</b>} {n.quoi} <b>{n.reference}</b>
          </span>
          {n.libelle && (
            <span
              style={{
                fontSize: telephone ? 14 : 13,
                lineHeight: telephone ? '19px' : '18px',
                color: 'var(--color-text-secondary)',
                ...(telephone ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
              }}
            >
              {n.libelle}
            </span>
          )}
          {n.extrait && (
            <span
              style={{
                marginTop: 3,
                fontSize: telephone ? 14 : 13,
                lineHeight: telephone ? '19px' : '18px',
                color: 'var(--color-text-secondary)',
                backgroundColor: 'var(--color-gray-5)',
                borderRadius: 6,
                padding: '6px 8px',
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
              }}
            >
              {n.extrait}
            </span>
          )}
          <span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--color-text-secondary)' }}>{piedNotification(n, maintenant)}</span>
        </span>
      </button>
      {!lectureSeule && (
        <button
          type="button"
          onClick={() => effacerDeLaCloche([n.id])}
          aria-label={`Effacer la notification ${n.reference}`}
          title="Effacer"
          className="ligne-cloche-effacer flex items-center justify-center"
          style={{
            position: 'absolute',
            top: telephone ? 8 : 6,
            right: telephone ? 4 : 6,
            width: 40,
            height: 40,
            borderRadius: 20,
            border: 'none',
            background: 'transparent',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  )
}

function Contenu({
  notifications,
  lectureSeule,
  telephone,
  onOuvrir,
  onPage,
}: {
  notifications: readonly NotificationAppli[]
  lectureSeule: boolean
  telephone: boolean
  onOuvrir: (n: NotificationAppli) => void
  onPage: (chemin: string) => void
}): JSX.Element {
  const maintenant = new Date()
  const { aujourdhui, avant } = parJour(notifications, maintenant)
  const marge = telephone ? 16 : 18
  const titre = (t: string, haut: number): JSX.Element => (
    <div style={{ ...PETITES_CAPITALES, padding: `${haut}px ${marge}px 4px` }}>{t}</div>
  )
  const ligne = (n: NotificationAppli): JSX.Element => (
    <Ligne key={n.id} n={n} maintenant={maintenant} lectureSeule={lectureSeule} telephone={telephone} onOuvrir={onOuvrir} />
  )
  const bouton = (libelle: string, icone: JSX.Element, chemin: string): JSX.Element => (
    <button
      type="button"
      onClick={() => onPage(chemin)}
      className="bouton-pied-cloche flex items-center justify-center"
      style={{
        flex: '1 1 0',
        gap: 8,
        minHeight: telephone ? 48 : 40,
        borderRadius: telephone ? 10 : 8,
        border: 'none',
        backgroundColor: 'var(--color-gray-5)',
        fontFamily: 'inherit',
        fontSize: telephone ? 15 : 13,
        fontWeight: 600,
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
      }}
    >
      {icone}
      {libelle}
    </button>
  )
  const boutons = (
    <>
      {bouton('Ce que vous suivez', <Eye size={16} aria-hidden="true" />, '/suivis')}
      {bouton('Régler mes notifications', <SlidersHorizontal size={16} aria-hidden="true" />, '/notifications')}
    </>
  )

  return (
    <>
      <div style={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
        {notifications.length === 0 ? (
          <p style={{ margin: 0, padding: `24px ${marge}px 8px`, fontSize: 14, lineHeight: '20px', color: 'var(--color-text-secondary)' }}>
            Rien de nouveau. Les mentions, les nouveautés sur ce que vous suivez et vos actions FNC-T arrivent ici.
          </p>
        ) : (
          <>
            {aujourdhui.length > 0 && titre('Aujourd’hui', 6)}
            {aujourdhui.map(ligne)}
            {avant.length > 0 && titre('Plus tôt', aujourdhui.length > 0 ? 14 : 6)}
            {avant.map(ligne)}
          </>
        )}
        <p style={{ margin: 0, padding: `12px ${marge}px 14px`, fontSize: 12, lineHeight: '17px', color: 'var(--color-text-secondary)' }}>
          Les notifications de plus de {DUREE_VIE_JOURS} jours s’effacent d’elles-mêmes.
        </p>
        {telephone && (
          <div className="flex flex-col" style={{ gap: 10, padding: '4px 16px 28px' }}>
            {boutons}
          </div>
        )}
      </div>
      {!telephone && (
        <div className="flex items-center" style={{ gap: 8, padding: '10px 12px', borderTop: '1px solid var(--color-gray-20)' }}>
          {boutons}
        </div>
      )}
    </>
  )
}

export function Cloche(): JSX.Element | null {
  const { visible, notifications, lectureSeule } = useCloche()
  const bureau = useMediaQuery(BUREAU)
  const navigate = useNavigate()
  const ouvrirFiche = useOuvrirFiche()
  const [ouvert, setOuvert] = useState(false)
  const ancre = useRef<HTMLDivElement>(null)

  // Une notification de plus : la cloche sonne (pas au premier affichage).
  const nombreVu = useRef<number | null>(null)
  const [sonne, setSonne] = useState(0)
  useEffect(() => {
    if (!visible) return
    const avant = nombreVu.current
    nombreVu.current = notifications.length
    if (avant !== null && notifications.length > avant) setSonne((n) => n + 1)
  }, [visible, notifications.length])

  // Bureau : un clic ailleurs ou Échap referme le panneau.
  useEffect(() => {
    if (!ouvert) return
    function surClic(e: MouseEvent): void {
      if (bureau && ancre.current && !ancre.current.contains(e.target as Node)) setOuvert(false)
    }
    function surTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOuvert(false)
    }
    document.addEventListener('mousedown', surClic)
    document.addEventListener('keydown', surTouche)
    return () => {
      document.removeEventListener('mousedown', surClic)
      document.removeEventListener('keydown', surTouche)
    }
  }, [ouvert, bureau])

  if (!visible) return null

  const nombre = notifications.length
  const libelle = nombre > 0 ? `Notifications, ${nombre} nouvelle${nombre > 1 ? 's' : ''}` : 'Notifications'

  function aller(chemin: string): void {
    setOuvert(false)
    if (bureau && PREFIXES_FICHES.some((p) => chemin.startsWith(p) && chemin.length > p.length)) ouvrirFiche(chemin)
    else navigate(chemin)
  }
  function ouvrirNotification(n: NotificationAppli): void {
    effacerDeLaCloche([n.id])
    if (n.lien) aller(n.lien)
  }
  const toutEffacer = !lectureSeule && nombre > 0 && (
    <button
      type="button"
      onClick={() => effacerDeLaCloche(notifications.map((n) => n.id))}
      className="bouton-texte-cloche"
      style={{
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: bureau ? 13 : 14,
        color: 'var(--color-text-secondary)',
        minHeight: bureau ? 36 : 44,
        padding: '0 10px',
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      Tout effacer
    </button>
  )
  const contenu = (
    <Contenu notifications={notifications} lectureSeule={lectureSeule} telephone={!bureau} onOuvrir={ouvrirNotification} onPage={aller} />
  )

  return (
    <div ref={ancre} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-label={libelle}
        title={libelle}
        aria-expanded={ouvert}
        className="flex items-center justify-center"
        style={{
          position: 'relative',
          width: 44,
          height: 44,
          borderRadius: 22,
          border: 'none',
          backgroundColor: ouvert && bureau ? 'var(--color-black)' : 'transparent',
          color: ouvert && bureau ? 'var(--color-white)' : 'var(--color-black)',
          cursor: 'pointer',
        }}
      >
        <Bell key={sonne} size={19} className={sonne > 0 ? 'cloche-sonne' : undefined} />
        {nombre > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              minWidth: 18,
              height: 18,
              boxSizing: 'border-box',
              padding: '0 5px',
              borderRadius: 9,
              border: '2px solid var(--color-white)',
              backgroundColor: 'var(--color-traffic-red)',
              color: 'var(--color-white)',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            {nombre > 99 ? '99+' : nombre}
          </span>
        )}
      </button>

      {ouvert && bureau && (
        <section
          aria-label="Notifications"
          className="flex flex-col"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: -8,
            width: 430,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'min(700px, calc(100dvh - 100px))',
            zIndex: 70,
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-gray-20)',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)',
            overflow: 'hidden',
            color: 'var(--color-text-primary)',
          }}
        >
          <div className="flex items-center" style={{ gap: 8, padding: '14px 12px 10px 18px' }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, flexGrow: 1 }}>Notifications</h2>
            {toutEffacer}
          </div>
          {contenu}
        </section>
      )}

      {ouvert &&
        !bureau &&
        createPortal(
          <section
            aria-label="Notifications"
            role="dialog"
            aria-modal="true"
            className="flex flex-col"
            style={{ position: 'fixed', inset: 0, zIndex: 90, backgroundColor: 'var(--color-white)', color: 'var(--color-text-primary)' }}
          >
            <header className="flex items-center" style={{ gap: 4, padding: '8px 8px 8px 4px', borderBottom: '1px solid var(--color-gray-20)', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setOuvert(false)}
                aria-label="Fermer les notifications"
                className="flex items-center justify-center"
                style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: 'var(--color-black)', cursor: 'pointer' }}
              >
                <ChevronLeft size={24} />
              </button>
              <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700, flexGrow: 1 }}>Notifications</h1>
              {toutEffacer}
            </header>
            {contenu}
          </section>,
          document.body,
        )}
    </div>
  )
}
